/**
 * Subscribers Admin Hooks
 *
 * React Query wrappers around the admin-gated /api/admin/subscribers/*
 * routes defined in `routes/workflows/subscribers-admin-workflow.ts`.
 *
 * The keys are namespaced under ["subscribers-admin", ...] so mutations
 * can invalidate the whole admin surface without touching unrelated
 * caches (e.g. the public courses-landing signup form).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/axios";

export type SubscriberStatus =
  | "pending"
  | "confirmed"
  | "unsubscribed"
  | "bounced"
  | "complained"
  // Paid (Stripe-style) statuses surfaced when the row's kind is "paid":
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired";

export type SubscriberKind = "email" | "paid";

export interface AdminSubscriber {
  id: string;
  kind: SubscriberKind;
  email: string;
  status: SubscriberStatus | string;
  source: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  confirmed_at: string | null;
  unsubscribed_at: string | null;
  created_at: string;
  updated_at: string;
  tags: string[];
  notes: string;
  last_emailed_at: string | null;
  total_emails_sent: number;
  ip_address: string | null;
  user_agent: string | null;
  /** Only populated when kind === "paid". */
  subscription?: {
    plan_name: string | null;
    billing_cycle: string | null;
    current_period_end: string | null;
    trial_end: string | null;
    canceled_at: string | null;
    last_payment_amount: number | null;
    last_payment_date: string | null;
  };
}

export interface ListFilters {
  q?: string;
  status?: SubscriberStatus | string | "all";
  source?: string;
  tag?: string;
  hasName?: boolean;
  /** Restrict the union: "email" only, "paid" only, or "all" (default). */
  type?: SubscriberKind | "all";
}

export interface ListParams extends ListFilters {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface ListResponse {
  rows: AdminSubscriber[];
  total: number;
  page: number;
  pageSize: number;
  facets: {
    statuses: Array<{ status: string; count: number }>;
    sources: Array<{ source: string; count: number }>;
    tags: Array<{ tag: string; count: number }>;
  };
  counts?: {
    email: number;
    paid: number;
  };
}

export interface SubscriberStats {
  total: number;
  confirmed: number;
  unsubscribed: number;
  bounced: number;
  new_30d: number;
  new_7d: number;
  unsubscribed_30d: number;
  paid?: {
    total: number;
    active: number;
    trialing: number;
    past_due: number;
    new_30d: number;
    canceled_30d: number;
  };
}

export interface SubscriberDetail extends AdminSubscriber {
  activity: {
    enrollments: number;
    event_rsvps: number;
    ai_conversations: number;
  };
}

const STALE_MS = 30 * 1000;

function buildParams(p: ListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (p.q) out.q = p.q;
  if (p.status && p.status !== "all") out.status = p.status;
  if (p.source) out.source = p.source;
  if (p.tag) out.tag = p.tag;
  if (p.hasName) out.hasName = "true";
  if (p.type && p.type !== "all") out.type = p.type;
  if (p.page) out.page = p.page;
  if (p.pageSize) out.pageSize = p.pageSize;
  if (p.sortBy) out.sortBy = p.sortBy;
  if (p.sortDir) out.sortDir = p.sortDir;
  return out;
}

export function useSubscribersList(params: ListParams) {
  return useQuery({
    queryKey: ["subscribers-admin", "list", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ListResponse>(
        "/api/admin/subscribers",
        { params: buildParams(params) },
      );
      return data;
    },
    staleTime: STALE_MS,
    placeholderData: (prev) => prev,
  });
}

export function useSubscribersStats() {
  return useQuery({
    queryKey: ["subscribers-admin", "stats"],
    queryFn: async () => {
      const { data } = await apiClient.get<SubscriberStats>(
        "/api/admin/subscribers/stats",
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}

export function useSubscriberDetail(id: string | null) {
  return useQuery({
    queryKey: ["subscribers-admin", "detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<SubscriberDetail>(
        `/api/admin/subscribers/${id}`,
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}

export interface UpdateSubscriberPayload {
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  status?: SubscriberStatus;
  source?: string;
  tags?: string[];
  notes?: string;
}

export function useUpdateSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSubscriberPayload;
    }) => {
      const { data } = await apiClient.patch<AdminSubscriber>(
        `/api/admin/subscribers/${id}`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["subscribers-admin"] });
      qc.invalidateQueries({
        queryKey: ["subscribers-admin", "detail", vars.id],
      });
    },
  });
}

export function useDeleteSubscriber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<{ success: boolean }>(
        `/api/admin/subscribers/${id}`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers-admin"] });
    },
  });
}

export type BulkAction = "add_tag" | "remove_tag" | "set_status" | "delete";

export function useBulkSubscriberAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      ids: string[];
      action: BulkAction;
      tag?: string;
      status?: SubscriberStatus;
    }) => {
      const { data } = await apiClient.post<{
        success: boolean;
        affected: number;
      }>("/api/admin/subscribers/bulk", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers-admin"] });
    },
  });
}

export function useSendIndividualEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      subject: string;
      html: string;
      fromName?: string;
    }) => {
      const { data } = await apiClient.post<{
        success: boolean;
        skipped?: boolean;
        message: string;
      }>("/api/admin/subscribers/email/individual", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers-admin"] });
    },
  });
}

export interface BroadcastPreviewResponse {
  recipientCount: number;
  sampleRecipients: Array<{
    email: string;
    first_name: string | null;
    last_name: string | null;
    company: string | null;
  }>;
  requiresConfirm: boolean;
  confirmToken: string;
  confirmTokenExpiresAt: string;
}

export function useBroadcastPreview() {
  return useMutation({
    mutationFn: async (payload: { filters?: ListFilters; ids?: string[] }) => {
      const { data } = await apiClient.post<BroadcastPreviewResponse>(
        "/api/admin/subscribers/email/broadcast/preview",
        payload,
      );
      return data;
    },
  });
}

export interface BroadcastResponse {
  success: boolean;
  test?: boolean;
  broadcastId?: string;
  recipientCount?: number;
  sent?: number;
  failed?: number;
  failures?: Array<{ email: string; reason: string }>;
  elapsedMs?: number;
  message?: string;
}

export function useBroadcastSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      subject: string;
      html: string;
      fromName?: string;
      filters?: ListFilters;
      ids?: string[];
      confirmToken?: string;
      testRecipient?: string;
    }) => {
      const { data } = await apiClient.post<BroadcastResponse>(
        "/api/admin/subscribers/email/broadcast",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscribers-admin"] });
    },
  });
}

export interface AiDraftResponse {
  success: boolean;
  subject?: string;
  html?: string;
  model?: string;
  tokensUsed?: number;
  /**
   * {{tokens}} the model emitted that aren't in our documented merge-token
   * set. They will render literally to recipients if sent as-is.
   */
  unknownTokens?: string[];
  message?: string;
}

export function useAiDraftEmail() {
  return useMutation({
    mutationFn: async (payload: {
      prompt: string;
      currentSubject?: string;
      currentHtml?: string;
      audience?: string;
    }) => {
      const { data } = await apiClient.post<AiDraftResponse>(
        "/api/admin/subscribers/email/ai-draft",
        payload,
      );
      return data;
    },
  });
}

// ── Signup time-series (chart above the list) ─────────────────────────
export type SignupTimeseriesRange = "week" | "month" | "year" | "all";

export interface SignupTimeseriesPoint {
  /** Bucket start date in ISO YYYY-MM-DD form (day, ISO-week Monday, or month-1). */
  date: string;
  /** New email-list signups created in this bucket. */
  email: number;
  /** New paid subscriptions created in this bucket. */
  paid: number;
}

export interface SignupTimeseriesResponse {
  range: SignupTimeseriesRange;
  bucket: "day" | "week" | "month";
  series: SignupTimeseriesPoint[];
}

export function useSubscriberSignupTimeseries(range: SignupTimeseriesRange) {
  return useQuery({
    queryKey: ["subscribers-admin", "signup-timeseries", range],
    queryFn: async () => {
      const { data } = await apiClient.get<SignupTimeseriesResponse>(
        "/api/admin/subscribers/signup-timeseries",
        { params: { range } },
      );
      return data;
    },
    // Trends don't shift minute-to-minute; cache aggressively to keep tab
    // switches and range toggles instant.
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function buildCsvExportUrl(filters: ListFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.status && filters.status !== "all")
    params.set("status", filters.status);
  if (filters.source) params.set("source", filters.source);
  if (filters.tag) params.set("tag", filters.tag);
  if (filters.hasName) params.set("hasName", "true");
  if (filters.type && filters.type !== "all") params.set("type", filters.type);
  const qs = params.toString();
  return `/api/admin/subscribers/export.csv${qs ? `?${qs}` : ""}`;
}
